import { useEffect, useState } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { Logo } from "./Logo";
import { EASE } from "./Motion";

const NAV = [
  { to: "/services", id: "services", label: "Services" },
  { to: "/faq", id: "faq", label: "FAQ" },
  { to: "/contact", id: "contact", label: "Contact Us" },
];

export const Header = () => {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  return (
    <header className="fixed inset-x-0 top-0 z-50 border-b border-[#E2DCD0]/70 bg-[#FDFBF7]/85 backdrop-blur-xl" data-testid="site-header">
      <div className="mx-auto flex h-20 max-w-7xl items-center justify-between px-5 sm:px-8 lg:px-12">
        <Logo />
        <nav className="hidden items-center gap-10 md:flex" aria-label="Primary">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={`nav-${n.id}-link`}
              className={({ isActive }) =>
                `group relative text-sm font-medium transition-colors ${isActive ? "text-[#1E2621]" : "text-[#4A554D] hover:text-[#1E2621]"}`
              }
            >
              {({ isActive }) => (
                <>
                  {n.label}
                  <span className={`absolute -bottom-1.5 left-0 h-px bg-[#C86D51] transition-[width] duration-500 ${isActive ? "w-full" : "w-0 group-hover:w-full"}`} />
                </>
              )}
            </NavLink>
          ))}
          <Link
            to="/contact"
            data-testid="header-cta-button"
            className="group inline-flex items-center gap-2 rounded-full bg-[#1E2621] px-5 py-2.5 text-sm font-medium text-[#F4F1EA] transition-colors hover:bg-[#A9533A]"
          >
            Start a conversation
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
          </Link>
        </nav>
        <button
          className="flex h-11 w-11 items-center justify-center rounded-full border border-[#E2DCD0] md:hidden"
          onClick={() => setOpen((o) => !o)}
          aria-label="Toggle menu"
          aria-expanded={open}
          data-testid="mobile-menu-toggle"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>
      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.5, ease: EASE }}
            className="overflow-hidden border-t border-[#E2DCD0] bg-[#FDFBF7] md:hidden"
            data-testid="mobile-menu"
          >
            <div className="flex flex-col px-5 py-6">
              {NAV.map((n) => (
                <Link key={n.to} to={n.to} className="font-serif-display border-b border-[#E2DCD0] py-4 text-3xl" data-testid={`mobile-nav-${n.id}-link`}>
                  {n.label}
                </Link>
              ))}
              <Link to="/contact" className="mt-6 rounded-full bg-[#1E2621] px-5 py-3.5 text-center text-sm font-medium text-[#F4F1EA]" data-testid="mobile-cta-button">
                Start a conversation
              </Link>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
